/**
 * @fileoverview The "Save" sheet, once "Check and save" (D78 P3, mock-up frames 2, 5 and 7; B137 retitled it): the photos just added through "Take or upload a
 * photo", reviewed over the page the gardener is on, never on another tab. The whole screen on a phone (as the add
 * form is), a box from sm up.
 *
 * It draws the tray's own piles and cards (TrayPiles: the plant card, the seed-packet card, the several-kinds card,
 * the photo's sheet, the allowance notices), only those holding this batch's photos, one column. Under the title,
 * "3 photos in 2 groups". While the batch goes in, "Adding 2 of 4…"; a photo that failed to go in is said under it
 * (the standard sentence for a failure on our side, reported to Sentry). Photos from before are not
 * mentioned here (B63): they stay reachable from Today's "Sort the photos waiting" job and the Photos page. Closing
 * the sheet keeps every unfiled photo on the server, as the Photos page does; it closes by itself once every pile of
 * the batch is filed.
 *
 * Without the photo AI (Free) nothing is read: one quiet line at the top, "Name each photo yourself, or see what Grow
 * More adds" (TrayPiles, review.guessLocked), and every card asks plainly. No wall (P6).
 *
 * Where: usePhotoAdd (Plants, Beds, Seasons and Today).
 * API: GET /tray (useTray), only while the sheet is open (W24 Job C): mounted closed on Today, Plants, Beds and
 * Seasons, it reads nothing; TrayPiles' own (drawn only inside the open sheet).
 * Words (en/es): tray.pile.keptOnPage (the packet card's Cancel toast), review.title, tray.photosIn, tray.photosInOne, tray.photosInOneGroup, tray.uploading,
 * tray.uploadFailed, tray.loading.
 *
 * @module CheckAndSave
 */

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { FailureText } from '@/shared/components/ServerFailure';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useToast } from '@/shared/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { drawPiles, useTray } from '@/features/photo-tray/lib/useTray';
import type { BatchProgress } from '@/features/photo-tray/lib/useTrayBatch';
import { TrayPiles, type Filed } from '@/features/photo-tray/components/TrayPiles';

/** What the sheet is given. */
interface CheckAndSaveProps {
  /** Shown. */
  open: boolean;
  /** Closes it; the unfiled photos stay on the tray. */
  onClose: () => void;
  /** This batch's photos, by tray id, as they land. */
  batch: ReadonlySet<number>;
  /** True while the batch is going in. */
  uploading: boolean;
  /** How far the batch has got, and what failed. */
  progress: BatchProgress | null;
  /** Added from Beds' photo row: the cards start on "A new bed…". */
  newBed?: boolean;
  /** Told of every filing. */
  onFiled?: (filed: Filed) => void;
}

/**
 * The full-screen "Check and save" sheet over the page.
 *
 * @param props - CheckAndSaveProps.
 */
export const CheckAndSave: React.FC<CheckAndSaveProps> = ({ open, onClose, batch, uploading, progress, newBed = false, onFiled }) => {
  /** The app's words, in the gardener's language. */
  const { reactTranslate: t } = useTranslation();
  /** Says the Cancel's toast. */
  const { toast } = useToast();
  /** The seed-packet card's Cancel (B137 F1): closes the sheet as the X does, keeps the photo, and says where it is. */
  const keepPhotos = (): void => {
    toast({ title: t('tray.pile.keptOnPage') });
    onClose();
  };
  // The tray is read while the sheet is open only (W24 Job C): closed, it reads nothing.
  const { data: tray, isLoading } = useTray(open);
  /** Every pile on the tray, as drawn. */
  const all = React.useMemo(() => drawPiles(tray), [tray]);
  /** The piles holding this batch's photos. */
  const mine = all.filter((pile) => pile.photos.some((photo) => batch.has(photo.tray_id)));
  /** How many photos this batch holds. */
  const minePhotos = mine.reduce((sum, pile) => sum + pile.photos.length, 0);

  // Once every pile of the batch is filed (and nothing more is coming in), the sheet's work is done.
  const hadPiles = React.useRef(false);
  React.useEffect(() => {
    if (!open) { hadPiles.current = false; return; }
    if (mine.length > 0) hadPiles.current = true;
    else if (hadPiles.current && !uploading) { hadPiles.current = false; onClose(); }
  }, [open, mine.length, uploading, onClose]);

  /** The line saying which photos failed to go in, or null. */
  const failedLine = progress && progress.failed > 0 && !uploading ? (
    <p className="mb-3 text-xs text-destructive" aria-live="polite">
      {t('tray.uploadFailed', { count: progress.failed })}
      {progress.ours && <> <FailureText text={progress.ours} /></>}
      {progress.refused && <> {progress.refused}</>}
    </p>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        scrollBody={false}
        className={cn(
          'flex h-[100dvh] max-h-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0',
          'sm:h-[min(90dvh,760px)] sm:max-w-lg sm:rounded-lg sm:border',
        )}
      >
        <DialogHeader className="shrink-0 space-y-0.5 border-b px-4 pb-3 pt-4 pr-14 text-left">
          <DialogTitle className="text-lg">{t('review.title')}</DialogTitle>
          {mine.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {t(minePhotos === 1 ? 'tray.photosInOne' : mine.length === 1 ? 'tray.photosInOneGroup' : 'tray.photosIn', { photos: minePhotos, groups: mine.length })}
            </p>
          )}
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-4">
          {uploading && progress && (
            <GrowLoader variant="block" label={t('tray.uploading', { done: progress.done, total: progress.total })} className="mb-3" />
          )}
          {failedLine}
          {isLoading && !uploading ? <GrowLoader variant="block" label={t('tray.loading')} className="py-8" /> : (
            <TrayPiles only={batch} uploading={uploading} variant="sheet" newBed={newBed} onFiled={onFiled} onKeepPhotos={keepPhotos} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
